import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, SafeAreaView, Platform, Alert, ActivityIndicator, Image } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore } from '../store/useAppStore';
import { Colors } from '../constants/theme';
import ScreenBackground from '../components/ScreenBackground';
import { mensajeDeError } from '../utils/mensajeDeError';
// Seguro en Android y en web: la libreria envuelve el modulo nativo en un
// `if (Platform.OS === 'ios')` y declara `platforms: ["apple"]`, asi que el
// autolinking de Gradle ni lo ve. Ver la nota larga en services/appleSignIn.ts.
import * as AppleAuthentication from 'expo-apple-authentication';
import { auth, db, getNativeAuth } from '../services/firebaseConfig';
// SDK WEB — solo usado cuando Platform.OS === 'web' (portal owner navegador).
import {
  signInWithEmailAndPassword as webSignInWithEmailAndPassword,
  signOut as webSignOut,
  sendEmailVerification as webSendEmailVerification,
  GoogleAuthProvider as WebGoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
// SDK NATIVO — usado en Android/iOS. El SDK web cuelga en RN+Hermes
// (mismo bug ya documentado en firebaseConfig.ts).
import {
  signInWithEmailAndPassword as nativeSignInWithEmailAndPassword,
  signOut as nativeSignOut,
  GoogleAuthProvider as NativeGoogleAuthProvider,
  signInWithCredential as nativeSignInWithCredential,
} from '@react-native-firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Forma mínima que satisfacen tanto el `User` del SDK web como el
// `FirebaseAuthTypes.User` del SDK nativo — evita acoplarnos a uno solo.
type AnyAuthUser = {
  uid: string;
  email: string | null;
  phoneNumber: string | null;
  displayName: string | null;
  // Los dos SDK lo traen. Opcional a proposito: si algun dia llega `undefined`,
  // el muro de abajo NO bloquea. No se puede medir cuantos duenos tienen el
  // correo sin verificar, asi que ante la duda se deja pasar: dejar fuera de su
  // panel a alguien que paga es peor que el hueco que esto cierra.
  emailVerified?: boolean;
};

const isWebProduction =
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  !window.location.hostname.includes('localhost');

export default function PartnerLoginScreen() {
  const router = useRouter();
  const setLogin = useAppStore((state) => state.setLogin);
  const setRole = useAppStore((state) => state.setRole);
  const setBaseRole = useAppStore((state) => state.setBaseRole);
  const setProfile = useAppStore((state) => state.setProfile);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showError = (msg: string) => {
    setError(msg);
    if (Platform.OS !== 'web') Alert.alert('Acceso denegado', msg);
  };

  /**
   * Valida que el usuario autenticado sea RENT_CAR_OWNER y, si lo es, hidrata
   * el store + navega al panel. Si no, signOut + error visible.
   * Compartido entre login con correo y login con Google, web y nativo.
   *
   * `doSignOut` se pasa por parámetro para que cada plataforma cierre sesión
   * con su propio SDK (web o nativo) sin que esta función tenga que importar
   * ambos.
   */
  const finalizeOwnerLogin = async (
    user: AnyAuthUser,
    doSignOut: () => Promise<void>
  ): Promise<boolean> => {
    // Timeout 8s: el SDK web de Firestore puede colgarse silenciosamente en
    // RN+Hermes en algunos devices. Sin esto el botón "Ingresar" se queda
    // girando para siempre y el dueño piensa que la app está rota.
    const docPromise = getDoc(doc(db, 'users', user.uid));
    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), 8000)
    );
    const userDoc: any = await Promise.race([docPromise, timeoutPromise]);
    if (!userDoc) {
      await doSignOut().catch(() => {});
      showError('No pudimos validar tu cuenta (timeout). Verifica tu conexión e intenta de nuevo.');
      return false;
    }

    // ── EL MURO DEL CORREO, TAMBIEN AQUI ──────────────────────────────────
    //
    // La regla del fundador es «al entrar, cada correo debe estar verificado».
    // En el telefono se cumple: _layout.tsx:596 manda a /verify-email a quien
    // no lo tenga. Pero la linea de arriba (:594) hace
    // `if (Platform.OS === 'web') return;`, asi que los dos portales web eran
    // la puerta por donde esa regla no pasaba.
    //
    // VA ANTES DEL ROL a proposito: es mas barato y mas basico. Y no deja a
    // nadie tirado — le reenvia el enlace en el momento, para que no tenga que
    // ir a buscar el telefono. Quien entra con Google trae el correo verificado
    // de fabrica y no ve nada de esto.
    if (user.emailVerified === false) {
      let seEnvio = false;
      try {
        const actual = auth.currentUser;
        if (actual) {
          await webSendEmailVerification(actual);
          seEnvio = true;
        }
      } catch (e: any) {
        console.warn('[partner] no se pudo reenviar la verificacion:', e?.message);
      }
      await doSignOut().catch(() => {});
      showError(
        seEnvio
          ? `Tienes que verificar tu correo antes de entrar al panel. Te acabamos de enviar el enlace a ${user.email ?? 'tu correo'}: abrelo y vuelve a entrar aqui.`
          : 'Tienes que verificar tu correo antes de entrar al panel. Abre el enlace que te enviamos al registrarte, o verificalo desde la app movil.'
      );
      return false;
    }

    const role = userDoc.exists() ? (userDoc.data().role as string | undefined) : undefined;
    if (role !== 'RENT_CAR_OWNER') {
      await doSignOut().catch(() => {});
      showError(
        'Este portal es exclusivo para Dueños de Rent Car. Si eres pasajero o chofer, usa la app móvil.'
      );
      return false;
    }

    setLogin(true);
    setRole('RENT_CAR_OWNER');
    setBaseRole('RENT_CAR_OWNER');
    const data = userDoc.data();
    const profileName = data?.first_name
      ? `${data.first_name} ${data.last_name ?? ''}`.trim()
      : (user.displayName ?? '');
    setProfile(profileName, user.phoneNumber ?? '', user.email ?? '');
    router.replace('/(tabs)/dashboard');
    return true;
  };

  const handleLogin = async () => {
    setError(null);
    if (!email.trim() || !password) {
      showError('Ingresa tu correo y contraseña.');
      return;
    }
    setLoading(true);
    try {
      if (Platform.OS === 'web') {
        const cred = await webSignInWithEmailAndPassword(auth, email.trim(), password);
        await finalizeOwnerLogin(cred.user, () => webSignOut(auth));
      } else {
        // El SDK web cuelga en RN+Hermes (timeout silencioso contra
        // identitytoolkit). Usamos el SDK nativo + timeout 15s defensivo
        // — mismo patrón que auth.tsx.
        const nativeAuth = getNativeAuth();
        const authPromise = nativeSignInWithEmailAndPassword(nativeAuth, email.trim(), password);
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('TIMEOUT_15S')), 15000)
        );
        const cred: any = await Promise.race([authPromise, timeoutPromise]);
        await finalizeOwnerLogin(cred.user, () => nativeSignOut(nativeAuth));
      }
    } catch (e: any) {
      console.warn('Partner login error:', e?.code, e?.message);
      const code = String(e?.code ?? '');
      const msg = String(e?.message ?? '');
      if (msg === 'TIMEOUT_15S') {
        showError('El servidor no respondió en 15 segundos. Verifica tu conexión a internet.');
      } else if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
        showError('Credenciales inválidas. Verifica tu correo y contraseña.');
      } else if (code.includes('too-many-requests')) {
        showError('Demasiados intentos. Espera unos minutos antes de intentar nuevamente.');
      } else if (code.includes('network-request-failed')) {
        showError('Sin conexión a internet. Verifica tu Wi-Fi o datos móviles.');
      } else {
        showError('No se pudo iniciar sesión. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── ENTRAR CON APPLE (guideline 4.8) ─────────────────────────────────
  // Esta pantalla ofrece Google, y Apple exige que donde haya login de un
  // tercero haya tambien una opcion que no comparta datos. Es alcanzable
  // desde el movil —`(tabs)/_layout.tsx` manda aqui a quien no es owner—, asi
  // que un revisor de Apple puede acabar justo en esta pantalla.
  //
  // En Android y en web `appleDisponible()` devuelve false sin tocar el modulo
  // nativo: el boton no se pinta y esta pantalla queda como esta hoy.
  const [appleListo, setAppleListo] = useState(false);
  useEffect(() => {
    // ⚠️ EL CORTE VA AQUÍ, ANTES DEL `import()`.
    //
    // ESTA es la pantalla de la web: la raíz redirige a /partner. El
    // `appleDisponible()` de dentro sí corta por plataforma, pero nunca
    // llegaba a correr: `appleSignIn.ts` importa
    // `expo-apple-authentication` de forma ESTÁTICA, y el bundle web no trae
    // ese módulo nativo. El chunk se bajaba (medido: 200 OK) y reventaba al
    // evaluarse, con un «Requiring unknown module» que el reportero global
    // apuntaba como FATAL en CADA visita a la web.
    //
    // Estaba así en producción antes de tocar nada — comprobado en vivo, con
    // otro número de módulo (el id cambia en cada compilación). Android y la
    // APK no se enteran: el botón de Apple solo existe en iOS, y allí el
    // módulo sí está.
    if (Platform.OS !== 'ios') return;
    let vivo = true;
    import('../services/appleSignIn')
      .then(({ appleDisponible }) => appleDisponible())
      .then((hay) => { if (vivo) setAppleListo(hay); })
      .catch(() => { /* sin Apple, quedan Google y el correo */ });
    return () => { vivo = false; };
  }, []);

  const handleAppleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const { entrarConApple } = await import('../services/appleSignIn');
      // ⚠️ `guardarPerfil: false` A PROPOSITO. Aqui no se crean cuentas: el
      // portal valida el rol que YA tiene el documento. Ver la nota en
      // services/appleSignIn.ts.
      const r = await entrarConApple('PASSENGER', { guardarPerfil: false });
      if (r.cancelado) return;

      const nativeAuth = getNativeAuth();
      const usuario = nativeAuth.currentUser;
      if (!usuario) {
        showError('No pudimos validar tu cuenta. Intenta de nuevo.');
        return;
      }
      // El MISMO portero que usan Google y el correo: si no es dueno de Rent
      // Car, se le cierra la sesion y se le explica.
      await finalizeOwnerLogin(usuario as any, () => nativeSignOut(nativeAuth));
    } catch (e: any) {
      const code = String(e?.code ?? '');
      console.warn('Apple login error:', code, e?.message);
      if (code === 'auth/account-exists-with-different-credential') {
        showError('Ya existe una cuenta con este correo usando otro método. Intenta con tu contraseña.');
      } else {
        showError(mensajeDeError(e, 'No se pudo iniciar sesión con Apple. Intenta con tu correo y contraseña.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Google login REAL en ambas plataformas. Antes mostraba un Alert hardcoded
  // ("solo desde la web") en móvil — eliminado. Ahora:
  //   web    → signInWithPopup + GoogleAuthProvider (SDK web).
  //   nativo → GoogleSignin (Android/iOS) + GoogleAuthProvider.credential
  //            + signInWithCredential (SDK nativo). El webClientId se configura
  //            UNA vez en firebaseConfig.ts.
  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      if (Platform.OS === 'web') {
        const provider = new WebGoogleAuthProvider();
        // Fuerza el selector de cuenta — paridad con el comportamiento Uber/inDriver.
        provider.setCustomParameters({ prompt: 'select_account' });
        const result = await signInWithPopup(auth, provider);
        await finalizeOwnerLogin(result.user, () => webSignOut(auth));
      } else {
        const { GoogleSignin } = require('@react-native-google-signin/google-signin');
        await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
        // Forzar selector de cuenta en cada login — equivalente al
        // `prompt: 'select_account'` que usa el web. Sin esto, si el device
        // ya tiene una sesión Google previa, GoogleSignin entra sin preguntar.
        try { await GoogleSignin.signOut(); } catch {}
        const signInResult: any = await GoogleSignin.signIn();
        // v15+ devuelve { type, data: { idToken } }; v13- devuelve { idToken } directo.
        const idToken: string | undefined =
          signInResult?.data?.idToken ?? signInResult?.idToken;
        if (!idToken) {
          throw new Error(
            'Google no devolvió idToken. Verifica EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID en tu .env (Firebase Console → Project Settings → Web client ID).'
          );
        }
        const credential = NativeGoogleAuthProvider.credential(idToken);
        const nativeAuth = getNativeAuth();
        const result: any = await nativeSignInWithCredential(nativeAuth, credential);
        await finalizeOwnerLogin(result.user, () => nativeSignOut(nativeAuth));
      }
    } catch (e: any) {
      console.warn('Google login error:', e?.code, e?.message);
      const code = String(e?.code ?? '');
      // Cancelaciones (web y nativo) — silenciosas, no son error real.
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'SIGN_IN_CANCELLED' ||
        code === '12501' ||
        code.includes('SIGN_IN_CANCELLED')
      ) {
        setError(null);
      } else if (code === 'auth/popup-blocked') {
        showError('El navegador bloqueó la ventana emergente. Habilita popups para este sitio.');
      } else if (code === 'auth/account-exists-with-different-credential') {
        showError('Ya existe una cuenta con este correo usando otro método. Intenta con tu contraseña.');
      } else {
        showError(
          mensajeDeError(
            e,
            'No se pudo iniciar sesión con Google. Verifica que Google Play Services esté actualizado.'
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.container}>
          <View style={styles.card}>
            <View style={styles.header}>
              <Ionicons name="business" size={48} color={Colors.primary} />
              <Text style={styles.title}>Go Safely Partners</Text>
              <Text style={styles.subtitle}>Panel de Control para Rent Car</Text>
              <Text style={styles.noticeText}>
                ⓘ Las cuentas se crean exclusivamente desde la app móvil.
                Este portal es solo para dueños ya verificados.
              </Text>
            </View>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <Ionicons name="mail-outline" size={20} color={Colors.textSecondary} style={styles.inputIcon} />
                <TextInput
                  style={styles.input}
                  placeholder="Correo electrónico corporativo"
                  placeholderTextColor={Colors.textSecondary}
                  value={email}
                  onChangeText={(t) => { setError(null); setEmail(t); }}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  editable={!loading}
                  onSubmitEditing={handleLogin}
                />
              </View>

              <View style={styles.inputContainer}>
                <Ionicons name="lock-closed-outline" size={20} color={Colors.textSecondary} style={styles.inputIcon} />
                <TextInput
                  style={styles.input}
                  placeholder="Contraseña"
                  placeholderTextColor={Colors.textSecondary}
                  value={password}
                  onChangeText={(t) => { setError(null); setPassword(t); }}
                  secureTextEntry
                  editable={!loading}
                  onSubmitEditing={handleLogin}
                />
              </View>

              {error && (
                <View style={styles.errorBox}>
                  <Ionicons name="alert-circle" size={16} color="#F44336" style={{ marginRight: 6 }} />
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}

              <TouchableOpacity
                style={[styles.loginBtn, loading && { opacity: 0.7 }]}
                onPress={handleLogin}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  <Text style={styles.loginBtnText}>Ingresar al Panel</Text>
                )}
              </TouchableOpacity>

              <View style={styles.dividerRow}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>o continúa con</Text>
                <View style={styles.dividerLine} />
              </View>

              {/* Apple primero, por la misma razon que en `auth.tsx`: la
                  guideline 4.8 pide que la opcion que no comparte datos no
                  quede en peor sitio que las demas. En Android y en web
                  `appleListo` es false y esto no se pinta. */}
              {appleListo && (
                <AppleAuthentication.AppleAuthenticationButton
                  buttonType={AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN}
                  buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.BLACK}
                  cornerRadius={12}
                  style={styles.appleBtn}
                  onPress={handleAppleLogin}
                />
              )}

              <TouchableOpacity
                style={[styles.googleBtn, loading && { opacity: 0.6 }]}
                onPress={handleGoogleLogin}
                disabled={loading}
              >
                <Image
                  source={{ uri: 'https://developers.google.com/identity/images/g-logo.png' }}
                  style={styles.googleLogo}
                />
                <Text style={styles.googleBtnText}>Continuar con Google</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.forgotBtn} onPress={() => {
                if (!email.trim()) {
                  showError('Ingresa tu correo arriba para enviarte el enlace de recuperación.');
                  return;
                }
                // Reset password. En web usamos el SDK web; en nativo el SDK
                // nativo (el web cuelga en RN+Hermes). Import dinámico para
                // no inflar el bundle inicial.
                if (Platform.OS === 'web') {
                  import('firebase/auth').then(({ sendPasswordResetEmail }) => {
                    sendPasswordResetEmail(auth, email.trim())
                      .then(() => window.alert('Te enviamos un enlace para restablecer la contraseña.'))
                      .catch(() => showError('No se pudo enviar el correo de recuperación.'));
                  });
                } else {
                  import('@react-native-firebase/auth').then(({ sendPasswordResetEmail }) => {
                    sendPasswordResetEmail(getNativeAuth(), email.trim())
                      .then(() => Alert.alert('Correo enviado', 'Revisa tu bandeja para restablecer la contraseña.'))
                      .catch(() => showError('No se pudo enviar el correo de recuperación.'));
                  });
                }
              }}>
                <Text style={styles.forgotText}>¿Olvidaste tu contraseña?</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* FIX AUDIT #5: gate por __DEV__ en vez de APP_VARIANT (que no se
              inyecta automáticamente en local Gradle builds). En cualquier
              APK release, __DEV__ = false y este bloque NO se renderiza.
              `isWebProduction` sigue cubriendo el web hosted como segunda capa. */}
          {!isWebProduction && __DEV__ && (
            <View style={{ marginTop: 30, alignItems: 'center' }}>
              <Text style={{ color: Colors.textSecondary, fontSize: 12, marginBottom: 10 }}>-- Solo para Pruebas (QA) --</Text>
              <TouchableOpacity
                onPress={() => {
                  setLogin(true);
                  setRole('PASSENGER');
                  setBaseRole('PASSENGER');
                  router.replace('/(tabs)/map');
                }}
                style={{ backgroundColor: 'rgba(0,0,0,0.05)', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 12 }}
              >
                <Text style={{ color: Colors.primary, fontSize: 13, fontWeight: 'bold' }}>Entrar como Invitado (App Pasajero)</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 450,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    padding: 40,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.1,
    shadowRadius: 30,
    elevation: 10,
    ...(Platform.OS === 'web' && {
      borderWidth: 1,
      borderColor: 'rgba(0,0,0,0.05)',
    } as any)
  },
  header: {
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontSize: 28,
    fontWeight: '900',
    color: Colors.text,
    marginTop: 15,
  },
  subtitle: {
    fontSize: 16,
    color: Colors.textSecondary,
    marginTop: 5,
  },
  noticeText: {
    fontSize: 12,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginTop: 12,
    paddingHorizontal: 8,
    lineHeight: 17,
  },
  form: {
    gap: 16,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F7FA',
    borderRadius: 16,
    paddingHorizontal: 15,
    borderWidth: 1,
    borderColor: '#E4E7EB',
  },
  inputIcon: {
    marginRight: 10,
  },
  input: {
    flex: 1,
    paddingVertical: 18,
    fontSize: 16,
    color: Colors.text,
  },
  loginBtn: {
    backgroundColor: Colors.primary,
    paddingVertical: 18,
    borderRadius: 16,
    alignItems: 'center',
    marginTop: 6,
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 15,
  },
  loginBtnText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
    letterSpacing: 0.5,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  errorText: {
    color: '#C62828',
    fontSize: 13,
    flex: 1,
  },
  forgotBtn: {
    alignSelf: 'center',
    paddingVertical: 8,
  },
  forgotText: {
    color: Colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    marginBottom: 14,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E4E7EB',
  },
  dividerText: {
    color: Colors.textSecondary,
    paddingHorizontal: 12,
    fontSize: 12,
    fontWeight: '600',
  },
  // Sin alto explicito el boton nativo de Apple NO SE VE (lo dice su propia
  // documentacion).
  appleBtn: {
    width: '100%',
    height: 50,
    marginBottom: 12,
  },
  googleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E4E7EB',
    paddingVertical: 14,
    borderRadius: 16,
    gap: 12,
  },
  googleLogo: {
    width: 20,
    height: 20,
  },
  googleBtnText: {
    color: Colors.text,
    fontSize: 15,
    fontWeight: 'bold',
  },
});
